<section role="region" aria-labelledby="relative-create-heading">
	<h2 id="relative-create-heading" lvrPadding [padHorizontal]="32" [padVertical]="0">
		{{ 'client.relatives.create.title' | transloco }}
	</h2>
	<form [formGroup]="form" class="form-container" lvrAutoSave="relative" [entityId]="draftId" [autoLoad]="false">
		<section class="form-grid">
			<!-- Person Category Field -->
			<lvr-field [label]="'client.relatives.create.category_person' | transloco | appendAsterisk">
				@let personCategoryCtrl = form.get('personCategoryId');
				<lvr-searchable-select
					formControlName="personCategoryId"
					[options]="personTypes()"
					[optionLabel]="personTypeLabel"
					[placeholder]="'base.field.select_please' | transloco"
					[searchPlaceholder]="'base.field.search_placeholder' | transloco"
					[noResultsText]="'base.field.no_results' | transloco"
					[ariaLabel]="'client.relatives.create.category_person' | transloco"
					[required]="true"
					[hasError]="hasError(personCategoryCtrl, 'required')"
					[errorMessage]="'base.form.required' | transloco"
				></lvr-searchable-select>
			</lvr-field>

			<!-- Remark Field -->
			@if (shouldDisplayRemarkField()) {
				<lvr-field [label]="'client.relatives.create.remark' | transloco">
					@let remarkCtrl = form.get('remark');
					<mat-form-field lvrInput [lvrError]="hasError(remarkCtrl, 'maxlength')">
						<input
							matInput
							maxlength="50"
							formControlName="remark"
							placeholder="Sonstiges Freitextfeld"
							[errorStateMatcher]="matcher"
						/>
						@if (hasError(remarkCtrl, 'maxlength')) {
							<mat-error>{{ 'base.form.error.remark_maxlength' | transloco: { max: 50 } }}</mat-error>
						}
						<mat-hint>{{
							'client.relatives.create.hint.maxlength' | transloco: { actual: remarkCtrl?.value?.length || 0, max: 50 }
						}}</mat-hint>
					</mat-form-field>
				</lvr-field>
			}

			<!-- Last Name Field -->
			<lvr-field [label]="'base.field.last_name' | transloco | appendAsterisk">
				@let nameCtrl = form.get('name');
				<mat-form-field lvrInput [lvrError]="hasError(nameCtrl, 'required', 'pattern')">
					<input
						matInput
						formControlName="name"
						maxlength="50"
						placeholder="Mustermann"
						aria-required="true"
						[errorStateMatcher]="matcher"
					/>
					@if (hasError(nameCtrl, 'required')) {
						<mat-error>{{ 'base.form.error.last_name_required' | transloco }}</mat-error>
					}
					@if (hasError(nameCtrl, 'pattern')) {
						<mat-error>{{ 'base.form.error.last_name_invalid' | transloco }}</mat-error>
					}
					<mat-hint>{{
						'client.relatives.create.hint.maxlength' | transloco: { actual: nameCtrl?.value?.length || 0, max: 50 }
					}}</mat-hint>
				</mat-form-field>
			</lvr-field>

			<!-- First Name Field -->
			<lvr-field [label]="'base.field.first_name' | transloco">
				@let firstNameCtrl = form.get('firstName');
				<mat-form-field lvrInput [lvrError]="hasError(firstNameCtrl, 'pattern')">
					<input
						matInput
						maxlength="50"
						formControlName="firstName"
						[placeholder]="'base.field.first_name' | transloco"
						[errorStateMatcher]="matcher"
					/>
					@if (hasError(firstNameCtrl, 'pattern')) {
						<mat-error>{{ 'base.form.error.first_name_invalid' | transloco }}</mat-error>
					}
					<mat-hint>{{
						'client.relatives.create.hint.maxlength' | transloco: { actual: firstNameCtrl?.value?.length || 0, max: 50 }
					}}</mat-hint>
				</mat-form-field>
			</lvr-field>

			<!-- Gender Field -->
			<lvr-field [label]="'base.field.gender' | transloco">
				<mat-form-field lvrInput>
					<mat-select
						formControlName="gender"
						[placeholder]="'base.field.select_please' | transloco"
						aria-required="true"
					>
						@for (option of genders(); track option.id) {
							<mat-option
								[value]="option.id"
								matTooltipClass="info-tooltip"
								[matTooltip]="
									option.salutation === 'divers u.a.' ? ('client.create_client.gender_hint_divers' | transloco) : null
								"
							>
								{{ option.salutation }}
							</mat-option>
						}
					</mat-select>
				</mat-form-field>
			</lvr-field>

			<!-- Title Field -->
			<lvr-field [label]="'base.field.title' | transloco">
				@let titleCtrl = form.get('title');
				<mat-form-field lvrInput [lvrError]="hasError(titleCtrl, 'pattern')">
					<input
						matInput
						formControlName="title"
						maxlength="30"
						placeholder="Dr., Prof. Dr."
						[errorStateMatcher]="matcher"
					/>
					@if (hasError(titleCtrl, 'pattern')) {
						<mat-error>{{ 'base.form.error.title_invalid' | transloco }}</mat-error>
					}
					<mat-hint>{{
						'client.relatives.create.hint.maxlength' | transloco: { actual: titleCtrl?.value?.length || 0, max: 30 }
					}}</mat-hint>
				</mat-form-field>
			</lvr-field>

			<lvr-address-completion formControlName="address" class="address-completion"></lvr-address-completion>
		</section>

		<!-- Contact Point Field -->
		@for (item of communicationWayCountArrayKeys(); track item) {
			<section class="form-grid">
				<h3 lvrPadding class="additional-communication-heading" [padVertical]="0" [padHorizontal]="8">
					@if (communicationWayCount() > 1) {
						<span>{{ 'base.field.contact_point.title' | transloco }}</span>
						<button matIconButton class="btn btn-tertiary" (click)="removeCommunicationWay($index)">
							<mat-icon class="material-symbols-outlined" aria-hidden="true">delete</mat-icon>
						</button>
					} @else {
						<span>{{ 'base.field.contact_point.title' | transloco }}</span>
					}
				</h3>
				<lvr-contact-point
					[contactPointCategories]="contactPointCategories()"
					class="address-completion"
					(values)="updateValues($event, $index)"
				></lvr-contact-point>
			</section>
		}
		<section class="form-grid">
			<!-- Add additional button -->
			<lvr-button
				lvrPadding
				[padHorizontal]="32"
				[padVertical]="0"
				variant="primary"
				[label]="'base.field.contact_point.title' | transloco"
				[ariaLabel]="'base.field.contact_point.add_communication' | transloco"
				[type]="'button'"
				(clicked)="addCommunicationWay()"
				icon="add"
			></lvr-button>
		</section>

		<section class="form-grid" lvrPadding [padHorizontal]="16">
			<!-- Supervisor Field -->
			<lvr-field [label]="'client.relatives.create.supervisor' | transloco">
				<br />
				<mat-checkbox
					formControlName="isSupervised"
					[attr.aria-label]="'client.relatives.create.supervisor_hint_yes' | transloco"
				>
					{{ 'client.relatives.create.supervisor_yes' | transloco }}
				</mat-checkbox>
			</lvr-field>

			<!-- Remarks for Supervised Field -->
			<lvr-field [label]="'client.relatives.create.other_text' | transloco">
				@let otherTextCtrl = form.get('otherText');
				<mat-form-field lvrInput>
					<input maxlength="2000" matInput formControlName="otherText" placeholder="ein Bemerkungstext zur Betreuung" />
					<mat-hint>{{
						'client.relatives.create.hint.maxlength'
							| transloco: { actual: otherTextCtrl?.value?.length || 0, max: 2000 }
					}}</mat-hint>
				</mat-form-field>
			</lvr-field>
		</section>

		<!-- Privacy Field -->
		<lvr-data-protection formControlName="clarification" [showAsterisk]="false"></lvr-data-protection>

		<lvr-default-actions
			[disableSave]="form.invalid || !canCreateClientEnvironmentsPermission()"
			[showLoading]="saving()"
			(save)="save()"
			(cancelSave)="cancel()"
		>
			<lvr-restore-actions entityType="relative" [entityId]="draftId"></lvr-restore-actions>
		</lvr-default-actions>
	</form>
</section>
